import React, { PropsWithChildren, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { useAsyncState, useGetProjectId, useIsMobile } from '@/utils/hooks';
import { getBuyerThemeInfo } from '@/services/buyer-dashboard';
import { extendTheme, RootStyle, Theme, useThemeStyle } from '@ygtechnologies/rubus-ui';
import { BuyerContext, BuyerContextData } from '@/buyer/BuyerContext';
import {
    BygrModule,
    buildWhiteLabelTheme,
    extractWhiteLabelTheme,
    LoadingIndicator,
    WhiteLabelDto,
    WhiteLabelEditor,
} from '@ygtechnologies/bygr-frontend-component-library';
import { saveProjectWhiteLabel } from '@/services/white-label';

export interface BuyerWhiteLabelProviderProps extends PropsWithChildren {}

function pruneEmpty<T extends Record<string, unknown>>(obj: T): Partial<T> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
        if (value != null && value !== '') {
            result[key] = value;
        }
    }
    return result as Partial<T>;
}

const accordionOverride = {
    hoverBackgroundColor: 'var(--rui-semantic-surface-subtle)',
    color: 'var(--rui-semantic-text-body)',
};

// Sidebar notification badge/dot are warning (yellow) by default; under white-label they follow
// the brand primary. Scoped to the warning variant so other warning-intent UI stays yellow.
const notificationBrandOverride = {
    badge: {
        warning: {
            solidBackgroundColor: 'var(--rui-semantic-intent-primary-surface)',
            solidTextColor: 'var(--rui-semantic-intent-primary-text)',
        },
    },
    dot: {
        warning: { color: 'var(--rui-semantic-intent-primary-surface)' },
    },
};

// Mode-agnostic CM-side CSS vars: hardcoded greys for legacy primary-shade slots,
// semantic-derived disabled/chart/content vars. Same in v1 and v2.
const sharedLegacyVars: Record<string, string> = {
    '--color-button-disabled': 'var(--rui-color-background-highlight)',
    '--color-button-disabled-contrast': 'var(--rui-color-text-disabled)',
    '--color-content-background': 'var(--rui-color-content-background, #fff)',
    '--chart-bar-color': 'var(--rui-color-primary)',
    '--chart-border-color': 'var(--rui-color-border)',
    '--chart-highlight-color': 'var(--rui-color-background-highlight)',
    '--color-primary': '#5c5c5c',
    '--color-primary-dark-10': '#5C5C5C',
    '--color-primary-dark-20': '#424242',
    '--color-primary-light-10': '#707070',
    '--color-primary-light-20': '#8F8F8F',
    '--color-primary-light-30': '#A8A8A8',
    '--color-primary-light-40': '#C2C2C2',
    '--color-primary-light-50': '#DBDBDB',
    '--color-primary-light-60': '#F5F5F5',
    '--color-primary-light-70': '#fff',
    '--color-primary-contrast': '#fff',
};

interface WhiteLabelStylesProps {
    whiteLabel: WhiteLabelDto;
}

const LegacyBuyerWhiteLabel: React.FC<WhiteLabelStylesProps> = ({ whiteLabel }) => {
    const theme = useMemo<Theme>(() => {
        if (!whiteLabel.isEnabled) return {};

        const baseTheme = buildWhiteLabelTheme(whiteLabel);
        const { sidebar } = extractWhiteLabelTheme(whiteLabel.colors);
        const milestone = pruneEmpty({
            activeColor: whiteLabel.colors.buttonColor,
            icon: whiteLabel.colors.buttonColorContrast,
            inactive: whiteLabel.colors.textMutedColor,
        });
        const menu = pruneEmpty({
            backgroundColor: sidebar.background,
            activeBackgroundColor: sidebar.highlight,
            textColor: sidebar.text,
        });

        return extendTheme(baseTheme, {
            override: {
                ...(Object.keys(milestone).length > 0 ? { milestone } : {}),
                ...(Object.keys(menu).length > 0 ? { menu } : {}),
                accordion: accordionOverride,
                ...notificationBrandOverride,
            },
        });
    }, [whiteLabel]);

    const { theme: mergedTheme, styles } = useThemeStyle(theme, { emitLegacyAliases: true });
    const primary = mergedTheme.color?.primary as Record<number, string> | undefined;

    return (
        <>
            <RootStyle value={styles} />
            {whiteLabel.isEnabled && (
                <RootStyle
                    value={{
                        '--color-button': primary?.[500],
                        '--color-button-focus': primary?.[400],
                        '--color-button-active': primary?.[300],
                        '--color-button-contrast': whiteLabel.colors?.buttonColorContrast || '#fff',
                        '--color-button-secondary': whiteLabel.colors.secondaryButtonColor,
                        '--color-button-secondary-contrast': whiteLabel.colors.secondaryButtonColorContrast,
                        '--color-banner': whiteLabel.colors.bannerColor,
                        '--color-banner-contrast': whiteLabel.colors.bannerColorContrast,
                        ...sharedLegacyVars,
                    }}
                />
            )}
        </>
    );
};

const V2BuyerWhiteLabel: React.FC<WhiteLabelStylesProps> = ({ whiteLabel }) => {
    const theme = useMemo<Theme>(() => {
        if (!whiteLabel.isEnabled) return {};
        return extendTheme(buildWhiteLabelTheme(whiteLabel), {
            override: { accordion: accordionOverride, ...notificationBrandOverride },
        });
    }, [whiteLabel]);

    const { theme: mergedTheme, styles } = useThemeStyle(theme, { emitLegacyAliases: true });
    const primary = mergedTheme.color?.primary as Record<number, string> | undefined;

    return (
        <>
            <RootStyle value={styles} />
            {whiteLabel.isEnabled && (
                <RootStyle
                    value={{
                        '--color-button': primary?.[500],
                        '--color-button-focus': primary?.[400],
                        '--color-button-active': primary?.[300],
                        '--color-button-contrast': 'var(--rui-semantic-intent-primary-text)',
                        '--color-button-secondary': 'var(--rui-semantic-intent-tertiary-surface)',
                        '--color-button-secondary-contrast': 'var(--rui-semantic-intent-tertiary-text)',
                        ...sharedLegacyVars,
                    }}
                />
            )}
        </>
    );
};

export const BuyerWhiteLabelProvider: React.FC<BuyerWhiteLabelProviderProps> = ({ children }) => {
    const isMobile = useIsMobile();
    const projectId = useGetProjectId();
    const permissions = useSelector((state: IState) => state.buyer.permissions);
    const sidebarMode = useSelector((state: IState) => state.global.sidebarMode);

    const [whiteLabel, setWhiteLabel, reloadWhiteLabel] = useAsyncState(
        () =>
            permissions?.residentialUnitId
                ? getBuyerThemeInfo(permissions.residentialUnitId, BygrModule.ChangeManagement)
                : Promise.resolve(null),
        [permissions?.residentialUnitId],
        null
    );

    const handleWhiteLabelSave = async (data: WhiteLabelDto) => {
        if (!projectId) {
            return;
        }

        await saveProjectWhiteLabel(projectId, data, null);
    };

    const buyerContextData = useMemo<BuyerContextData>(() => {
        return {
            hasCustomBackgroundColor: !!whiteLabel?.colors?.backgroundColor,
        };
    }, [whiteLabel]);

    const sidebarStyle = <RootStyle value={{ '--cm-sidebar-width': sidebarMode === 'drawer' ? '0px' : '304px' }} />;

    if (!whiteLabel) {
        return (
            <BuyerContext.Provider value={buyerContextData}>
                {sidebarStyle}
                <LoadingIndicator />
            </BuyerContext.Provider>
        );
    }

    const whiteLabelStyles = whiteLabel.themeJson ? (
        <V2BuyerWhiteLabel whiteLabel={whiteLabel} />
    ) : (
        <LegacyBuyerWhiteLabel whiteLabel={whiteLabel} />
    );

    return (
        <BuyerContext.Provider value={buyerContextData}>
            {sidebarStyle}
            {whiteLabelStyles}
            <RootStyle
                value={{
                    '--cm-background-color': 'var(--rui-color-background, #eef1f3)',
                    '--configurator-background': 'var(--rui-color-configurator-background, #eef1f3)',
                    '--color-content-background': 'var(--rui-color-content-background, #fff)',
                    '--buyer-max-content-width': '1600px',
                    '--cm-header-height': 'var(--cm-buyer-header-height-override, 118px)',
                    // Match the sidebar notification Dot exactly (yellow by default, brand under white-label).
                    '--color-notification': 'var(--rui-override-dot-warning-color, var(--rui-semantic-intent-warning-surface))',
                }}
            />
            {isMobile && <RootStyle value={{ '--cm-header-height': '174px' }} />}
            {children}
            {permissions?.impersonating && whiteLabel.editable && (
                <WhiteLabelEditor
                    value={whiteLabel}
                    onChange={(v) => setWhiteLabel({ ...v, editable: whiteLabel.editable })}
                    onSave={handleWhiteLabelSave}
                    onReset={reloadWhiteLabel}
                />
            )}
        </BuyerContext.Provider>
    );
};
